import { TriStateValueEnum } from '@/components';
import {
	BusinessType,
	DocTypeEnum,
	DocumentTypeEnum,
	MAPPING_DOC_TYPE,
	PageSize,
	PageTypeEnum,
} from '@/constant';
import * as API from '@/services';
import {
	formatWorkerApplicantAssignmentList,
	formatWorkerApplicantDetail,
	formatWorkerFranchiseDetail,
	formatWorkerLodgingDetail,
	formatWorkerProgramDetail,
	formatWorkerRecommenderDetail,
	IWorkerApplicantAssignmentListStore,
	IWorkerApplicantDetailStore,
	IWorkerApplicantLodgingStore,
	IWorkerFranchiseDetailStore,
	IWorkerLodgingDetailStore,
	IWorkerRecommenderDetailStore,
	TDefaultInfoUnitDetailStore,
} from '@/stores';
import { IDataMyPageStore } from '@/stores/my-page';
import { formatDataMyPage } from '@/stores/my-page/my-page.helper';

import { includes } from 'ramda';

import {
	NEED_GET_APPLICANT_DATA_DOCS,
	NEED_GET_APPLICANT_LIST_DATA_DOCS,
	NEED_GET_FRANCHISE_DATA_DOCS,
	NEED_GET_LODGING_DATA_DOCS,
	NEED_GET_LODGING_DATA_DOCS_WHEN_RELATED_INFO_HAS_LODGING_NON_FRANCHISE_AND_APPLICANT,
	NEED_GET_PROGRAM_DATA_DOCS,
	NEED_GET_RECOMMENDER_DATA_DOCS,
	REDUX_JSON,
} from './define-document-write.config';
import { PageDataUnion } from './define-document-write.type';

export type DetailDataType<T extends DocTypeEnum> =
	T extends keyof typeof REDUX_JSON
		? ReturnType<(typeof REDUX_JSON)[T]>
		: never;

type IGetReferenceData = {
	refTableName?: PageTypeEnum;
	refTableId?: number;
	mappingList?: Array<{ refTableName: PageTypeEnum; refTableId: number }>;
	documentType: DocumentTypeEnum;
} & PageDataUnion;

const getFranchiseId = (input: IGetReferenceData) => {
	const {
		docType,
		refTableId,
		mappingList,
		documentType,
		detailData,
		refTableName,
	} = input;

	let franchiseId: number | undefined;
	let secondFranchiseId: number | undefined;

	if (NEED_GET_FRANCHISE_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])) {
		if (
			docType === DocTypeEnum.WC1_13 &&
			refTableName === PageTypeEnum.CHANGE_WORK_PLACE
		) {
			franchiseId = (detailData as DetailDataType<typeof DocTypeEnum.WC1_13>)
				?.workerChangeWorkPlaceDetail?.newFranchiseId;
			return { franchiseId };
		}

		if (
			docType === DocTypeEnum.WC1_05 &&
			refTableName === PageTypeEnum.UNAUTHORIZED_DEPARTURE
		) {
			franchiseId = (detailData as DetailDataType<typeof DocTypeEnum.WC1_05>)
				?.workerUnauthorizedDeparture?.franchiseId;
			return { franchiseId };
		}

		if (
			docType === DocTypeEnum.WC1_05 &&
			refTableName === PageTypeEnum.CHANGE_WORK_PLACE
		) {
			franchiseId = (detailData as DetailDataType<typeof DocTypeEnum.WC1_05>)
				?.workerChangeWorkPlaceDetail?.newFranchiseId;
			return { franchiseId };
		}

		const franchiseList = mappingList?.filter(
			(item) => item.refTableName === PageTypeEnum.FRANCHISE
		);

		if (refTableName === PageTypeEnum.FRANCHISE) {
			franchiseId = refTableId;
			secondFranchiseId = franchiseList?.[0]?.refTableId;
		} else {
			franchiseId = franchiseList?.[0]?.refTableId;
			secondFranchiseId = franchiseList?.[1]?.refTableId;
		}
	}

	return { franchiseId, secondFranchiseId };
};

const getApplicantId = (input: IGetReferenceData) => {
	const {
		refTableName,
		refTableId,
		mappingList,
		documentType,
		detailData,
		docType,
	} = input;

	let applicantId: number | undefined;

	if (NEED_GET_APPLICANT_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])) {
		if (
			refTableName === PageTypeEnum.CHANGE_WORK_PLACE &&
			docType === DocTypeEnum.WC1_07
		) {
			return (detailData as DetailDataType<typeof DocTypeEnum.WC1_07>)
				?.applicantId;
		}

		if (
			refTableName === PageTypeEnum.UNAUTHORIZED_DEPARTURE &&
			docType === DocTypeEnum.WC1_05
		) {
			return (detailData as DetailDataType<typeof DocTypeEnum.WC1_05>)
				?.workerUnauthorizedDeparture?.applicantId;
		}

		if (
			refTableName === PageTypeEnum.CHANGE_WORK_PLACE &&
			docType === DocTypeEnum.WC1_05
		) {
			return (detailData as DetailDataType<typeof DocTypeEnum.WC1_05>)
				?.workerChangeWorkPlaceDetail?.applicantId;
		}

		applicantId =
			refTableName === PageTypeEnum.APPLICANT
				? refTableId
				: mappingList?.find?.(
						(item) => item.refTableName === PageTypeEnum.APPLICANT
					)?.refTableId;
	}

	return applicantId;
};

const getApplicantIds = (input: IGetReferenceData) => {
	const { refTableName, refTableId, mappingList, documentType } = input;

	let applicantIds =
		refTableName === PageTypeEnum.APPLICANT && refTableId ? [refTableId] : [];

	if (
		NEED_GET_APPLICANT_LIST_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])
	) {
		applicantIds = applicantIds?.concat(
			mappingList
				?.filter((item) => item.refTableName === PageTypeEnum.APPLICANT)
				?.map((item) => item.refTableId) || []
		);
	}

	return applicantIds;
};

const getLodgingId = (input: IGetReferenceData) => {
	const { refTableName, refTableId, mappingList, documentType } = input;

	// if documentType doesn't need get lodging data, return undefined
	if (!NEED_GET_LODGING_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])) {
		return undefined;
	}

	// if refTableName is LODGING, return refTableId
	if (refTableName === PageTypeEnum.LODGING) {
		return refTableId;
	}

	// if there is any relationship with Franchise or Applicant, don't get lodging
	if (
		NEED_GET_LODGING_DATA_DOCS_WHEN_RELATED_INFO_HAS_LODGING_NON_FRANCHISE_AND_APPLICANT.includes(
			MAPPING_DOC_TYPE[documentType]
		) &&
		mappingList?.some((item) =>
			[PageTypeEnum.FRANCHISE, PageTypeEnum.APPLICANT].includes(
				item.refTableName
			)
		)
	) {
		return undefined;
	}

	// if there is no relationship with Franchise or Applicant, find and return lodgingId
	return mappingList?.find((item) => item.refTableName === PageTypeEnum.LODGING)
		?.refTableId;
};

const getRecommenderId = (input: IGetReferenceData) => {
	const { refTableName, refTableId, mappingList, documentType } = input;

	let recommenderId: number | undefined;

	if (NEED_GET_RECOMMENDER_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])) {
		recommenderId =
			refTableName === PageTypeEnum.RECOMMENDER
				? refTableId
				: mappingList?.find?.(
						(item) => item.refTableName === PageTypeEnum.RECOMMENDER
					)?.refTableId;
	}

	return recommenderId;
};

export const splitForeignerNoToChars = (foreignerNo?: string) => {
	const result: Record<string, string> = {};
	for (let i = 1; i <= 13; i++) {
		result[`no${i}`] = foreignerNo?.charAt(i - 1) || '';
	}
	return result;
};
const getProgramId = (input: IGetReferenceData) => {
	const { refTableName, refTableId, mappingList, documentType } = input;

	let programId: number | undefined;

	if (NEED_GET_PROGRAM_DATA_DOCS.includes(MAPPING_DOC_TYPE[documentType])) {
		programId =
			refTableName === PageTypeEnum.PROGRAM
				? refTableId
				: mappingList?.find?.(
						(item) => item.refTableName === PageTypeEnum.PROGRAM
					)?.refTableId;
	}

	return programId;
};

export const getReferenceData = async (input: IGetReferenceData) => {
	const { franchiseId, secondFranchiseId } = getFranchiseId(input);
	const applicantId = getApplicantId(input);

	const lodgingId = getLodgingId(input);

	const recommenderId = getRecommenderId(input);
	const programId = getProgramId(input);

	const needSecondFranchise =
		[PageTypeEnum.APPLICANT, PageTypeEnum.FRANCHISE].includes(
			input.refTableName as PageTypeEnum
		) &&
		[
			DocumentTypeEnum.C_48,
			DocumentTypeEnum.C_58,
			DocumentTypeEnum.C_61,
		].includes(input.documentType);

	const needLatestAssignment =
		franchiseId &&
		includes(input.documentType, [
			DocumentTypeEnum.C_14,
			DocumentTypeEnum.C_60,
		]);

	// ARF_15 (계절근로 참여 추천서): the recommender-detail box is fed by the
	// franchise's recommender list API (/worker/franchise/{id}/recommender/list).
	const needFranchiseRecommenderList =
		!!franchiseId && input.docType === DocTypeEnum.ARF_15;

	const applicantIds = getApplicantIds(input);

	try {
		const [
			franchise,
			applicant,
			lodging,
			recommender,
			program,
			myPage,
			secondFranchise,
			applicantAssignmentList,
			latestAssignment,
			lastedAssignmentList,
			applicantList,
			applicantLodging,
			franchiseRecommenderList,
		] = await Promise.all([
			franchiseId
				? API.getWorkerFranchiseDetail(franchiseId, false)
				: Promise.resolve(undefined),
			applicantId
				? API.getWorkerApplicantDetail(applicantId, false)
				: Promise.resolve(undefined),

			lodgingId
				? API.getWorkerLodgingDetail(lodgingId, false)
				: Promise.resolve(undefined),
			recommenderId
				? API.getWorkerRecommenderDetail(recommenderId, false)
				: Promise.resolve(undefined),
			programId
				? API.getWorkerProgramDetail(programId, false)
				: Promise.resolve(undefined),
			input?.refTableName === PageTypeEnum.PROGRAM ||
			input?.refTableName === PageTypeEnum.UNIT
				? API.getDataMyPage()
				: Promise.resolve(undefined),
			needSecondFranchise && secondFranchiseId
				? API.getWorkerFranchiseDetail(secondFranchiseId, false)
				: Promise.resolve(undefined),
			applicantId
				? API.getWorkerApplicantAssignmentList({
						page: 1,
						id: applicantId,
						pageSize: 10,
					})
				: Promise.resolve(undefined),
			needLatestAssignment
				? API.getLatestAssignment(franchiseId)
				: Promise.resolve(undefined),
			applicantId
				? API.getWorkerLastedAssignment(applicantId)
				: Promise.resolve(undefined),
			applicantIds?.length
				? API.getApplicantFormInfo(applicantIds)
				: Promise.resolve(undefined),
			applicantId
				? API.getWorkerApplicantLodging(applicantId)
				: Promise.resolve(undefined),
			needFranchiseRecommenderList && franchiseId
				? API.getWorkerFranchiseRecommenderInfo(franchiseId)
				: Promise.resolve(undefined),
		]);

		const franchiseData = franchise?.data
			? formatWorkerFranchiseDetail(franchise?.data)
			: ({} as IWorkerFranchiseDetailStore);

		const applicantData = applicant?.data
			? formatWorkerApplicantDetail(applicant?.data)
			: ({} as IWorkerApplicantDetailStore);

		const lodgingData = lodging?.data
			? formatWorkerLodgingDetail(lodging?.data)
			: ({} as IWorkerLodgingDetailStore);

		const recommenderData = recommender?.data
			? formatWorkerRecommenderDetail(recommender?.data)
			: ({} as IWorkerRecommenderDetailStore);

		const programData = program?.data
			? formatWorkerProgramDetail(program?.data)
			: ({} as API.IProgram);

		const myPageData = myPage?.data
			? formatDataMyPage(myPage?.data)
			: ({} as IDataMyPageStore);

		const secondFranchiseData = secondFranchise?.data
			? formatWorkerFranchiseDetail(secondFranchise?.data)
			: ({} as IWorkerFranchiseDetailStore);

		const applicantAssignmentListData = applicantAssignmentList?.data
			? formatWorkerApplicantAssignmentList(
					applicantAssignmentList?.data?.content
				)
			: ([] as IWorkerApplicantAssignmentListStore[]);

		const lastedAssignment = lastedAssignmentList?.data
			? lastedAssignmentList?.data
			: ({} as API.GetLastedAssignmentResponse);

		const sortedApplicantListData = [...(applicantList?.data || [])].sort(
			(a, b) => {
				const indexA = applicantIds.indexOf(a.id);
				const indexB = applicantIds.indexOf(b.id);

				if (indexA === -1) return 1;
				if (indexB === -1) return -1;

				return indexA - indexB;
			}
		);
		const applicantLodgingData = applicantLodging?.data
			? applicantLodging?.data
			: ({} as IWorkerApplicantLodgingStore);

		const franchiseRecommenderListData = (franchiseRecommenderList?.data ||
			[]) as API.GetWorkerFranchiseRecommenderInfoListResponse;

		// ARF_15: the recommended-worker list is the applicant list of the first
		// recommender returned above (/worker/recommender/{id}/applicant/list).
		const firstRecommenderId = franchiseRecommenderListData?.[0]?.id;
		const recommenderApplicantList =
			needFranchiseRecommenderList && firstRecommenderId
				? await API.getWorkerRecommenderApplicantList({
						id: firstRecommenderId,
						page: 1,
						pageSize: PageSize.S,
					})
				: undefined;
		const recommenderApplicantListData =
			recommenderApplicantList?.data?.content || [];

		return {
			franchiseData,
			applicantData,
			lodgingData,
			recommenderData,
			programData,
			myPageData,
			secondFranchiseData,
			applicantAssignmentListData,
			latestAssignmentData: latestAssignment?.data,
			lastedAssignment,
			unitData: {} as TDefaultInfoUnitDetailStore,
			applicantListData: sortedApplicantListData,
			applicantLodgingData,
			franchiseRecommenderListData,
			recommenderApplicantListData,
		};
	} catch (error) {
		console.error('Error fetching reference data:', error);
		throw error;
	}
};

export const renderValuesFranchiseStatus = (
	index: number,
	type: BusinessType,
	detailData: API.GetWorkerProgramFranchiseStatusResponse
) => {
	switch (index) {
		case 0:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programFarmCnt
				: detailData?.[0]?.programFishingCnt;
		case 1:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programAgriculturalCoCnt
				: detailData?.[0]?.programCorporateFishingCnt;
		case 2:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programPublicFranchiseCnt
				: detailData?.[0]?.programFranchiseCnt;
		case 3:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programPublicFranchiseCnt +
						detailData?.[0]?.programFarmCnt +
						detailData?.[0]?.programAgriculturalCoCnt
				: detailData?.[0]?.programFranchiseCnt +
						detailData?.[0]?.programFishingCnt +
						detailData?.[0]?.programCorporateFishingCnt;
		default:
			return '0';
	}
};
export const renderValuesApplicantStatus = (
	index: number,
	type: BusinessType,
	detailData: API.GetWorkerProgramFranchiseStatusResponse
) => {
	switch (index) {
		case 0:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programFarmApplicantCnt
				: detailData?.[0]?.programFishingApplicantCnt;
		case 1:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programAgriculturalCoApplicantCnt
				: detailData?.[0]?.programCorporateFishingApplicantCnt;
		case 2:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programOtherCnt
				: detailData?.[0]?.programOtherApplicantCnt;
		case 3:
			return type === BusinessType.AGRICULTURAL
				? detailData?.[0]?.programOtherCnt +
						detailData?.[0]?.programFarmApplicantCnt +
						detailData?.[0]?.programAgriculturalCoApplicantCnt
				: detailData?.[0]?.programOtherApplicantCnt +
						detailData?.[0]?.programFishingApplicantCnt +
						detailData?.[0]?.programCorporateFishingApplicantCnt;
		default:
			return '0';
	}
};

// Helper function to get the next state of a 3-state checkbox
export const getNextStateOfTriStateCheckbox = (
	current: TriStateValueEnum
): TriStateValueEnum => {
	// Transition logic:
	// - If EMPTY (not clicked) -> CIRCLE (O)
	// - If CIRCLE (O) -> CROSS (X)
	// - If CROSS (X) -> CIRCLE (O)
	const transitions: Record<TriStateValueEnum, TriStateValueEnum> = {
		[TriStateValueEnum.EMPTY]: TriStateValueEnum.CIRCLE,
		[TriStateValueEnum.CIRCLE]: TriStateValueEnum.CROSS,
		[TriStateValueEnum.CROSS]: TriStateValueEnum.CIRCLE,
	};
	return transitions[current] || TriStateValueEnum.CIRCLE;
};
